import AppRouter from "@/router/AppRouter";
import { fetchResumeSession } from "@/shared/lib/fetchResumeSession";
import { setCustomerAuth } from "@/shared/redux/reducers/authSlice";
import { clearJourneyDataOnInactivity } from "@/shared/redux/reducers/paymentSuccessCleanup";
import { useAppDispatch } from "@/shared/redux/store/hooks";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { dispatchResumeJourney } from "@/shared/utils/dispatchResumeJourney";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import AppVersionChecker from "./shared/components/AppVersionCheckpoint";

const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000;

export default function App() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    auth: { user, customerAuth },
  } = usePosterReducers();
  const isLoggedIn = Boolean(user) || Boolean(customerAuth?.access_token);

  const location = useLocation();
  const inactivityTimerRef = useRef<number | null>(null);
  const initialDeepLinkStepRef = useRef<string | null>(null);
  const initialPathnameRef = useRef<string>(location.pathname);
  const initialSearchRef = useRef<string>(location.search);
  const processedResumeKeyRef = useRef<string | null>(null);

  const param = new URLSearchParams(location.search);
  const clientId = param.get("client_id");
  const resolvedLobId = param.get("lob_id");
  const accessToken = param.get("token");

  if (initialDeepLinkStepRef.current === null) {
    initialDeepLinkStepRef.current = param.get("step");
  }

  useEffect(() => {
    const loadResumeSession = async () => {
      try {
        if (!clientId || !resolvedLobId || !accessToken) return;

        const resumeKey = `${clientId}:${resolvedLobId}:${accessToken}`;
        if (processedResumeKeyRef.current === resumeKey) {
          return;
        }
        processedResumeKeyRef.current = resumeKey;

        const sessionData = await fetchResumeSession(clientId, accessToken, Number(resolvedLobId));

        // Deep link includes a bearer token; bootstrap retail auth so guarded quote flows
        // can continue without forcing OTP for the same user.
        dispatch(
          setCustomerAuth({
            access_token: accessToken,
            token_type: "Bearer",
            client: (sessionData?.client || {}) as any,
          } as any)
        );

        const { targetPath } = dispatchResumeJourney(dispatch, sessionData);

        const initialStep = initialDeepLinkStepRef.current;
        const nextParams = new URLSearchParams(initialSearchRef.current);
        // Token should never remain in the address bar/history after bootstrapping auth.
        nextParams.delete("token");

        if (initialStep) {
          nextParams.set("step", initialStep);
          navigate(`${initialPathnameRef.current}?${nextParams.toString()}`, { replace: true });
          return;
        }

        if (
          targetPath &&
          targetPath !== `${initialPathnameRef.current}${initialSearchRef.current}`
        ) {
          navigate(targetPath, { replace: true });
          return;
        }

        navigate(`${initialPathnameRef.current}?${nextParams.toString()}`, { replace: true });
      } catch (error) {
        console.error("Failed to load resume session:", error);
        const nextParams = new URLSearchParams(initialSearchRef.current);
        nextParams.delete("token");
        navigate(`${initialPathnameRef.current}?${nextParams.toString()}`, { replace: true });
      }
    };

    loadResumeSession();
  }, [clientId, resolvedLobId, accessToken, dispatch, navigate]);

  useEffect(() => {
    const getTimestamp = () => new Date().toLocaleString();

    const clearExistingTimer = () => {
      if (inactivityTimerRef.current !== null) {
        window.clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };

    if (isLoggedIn) {
      clearExistingTimer();
      console.log(`[InactivityTimer] skipped at ${getTimestamp()} because user is logged in.`);
      return;
    }

    const startTimer = (trigger: string) => {
      clearExistingTimer();
      const startedAt = new Date();
      const expiresAt = new Date(startedAt.getTime() + INACTIVITY_TIMEOUT_MS);

      console.log(
        `[InactivityTimer] started (${trigger}) at ${getTimestamp()} | expires at ${expiresAt.toLocaleString()}`
      );

      inactivityTimerRef.current = window.setTimeout(() => {
        dispatch(clearJourneyDataOnInactivity());
        console.log(
          `[InactivityTimer] timeout reached at ${getTimestamp()} | cleared travel, health, and motor journey step data plus payment and policy result data.`
        );
      }, INACTIVITY_TIMEOUT_MS);
    };

    const handleUserActivity = (event: Event) => {
      startTimer(event.type);
    };

    const events: Array<keyof WindowEventMap> = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((eventName) => {
      window.addEventListener(eventName, handleUserActivity, { passive: true });
    });

    // Route change is also considered user interaction for journey timeout reset.
    startTimer("route-change/init");

    return () => {
      clearExistingTimer();
      events.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserActivity);
      });
    };
  }, [dispatch, location.pathname, isLoggedIn]);

  return (
    <>
      <AppRouter />
      <Toaster />
      <AppVersionChecker />
    </>
  );
}
